import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import type { TransactionMappingItem } from 'lumen/models';
import type MarketSettingsTransactionMappingRoute from 'lumen/routes/market/settings/transaction-mapping';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import {
  type Category,
  FILTER_IDS,
  type FilterId,
  type SubCategory,
} from 'lumen/utils/constants/transaction-mapping/constants';
import { FILTER_TRANSLATION_KEYS } from 'lumen/utils/constants/transaction-mapping/translation-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export interface FilterOption {
  id: FilterId;
  label: string;
}

export default class MarketSettingsTransactionMappingController extends Controller {
  declare model: ModelFrom<MarketSettingsTransactionMappingRoute>;

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked filters: FilterOption[] = [];
  @tracked isOnlyUnmappedShown = false;

  saveTask = task({ drop: true }, async (): Promise<void> => {
    const mappingUrl = formatUrl(
      '/apigateway/v1/app/totalrevenue/update-mapping',
      { market_id: this.hotelAndMarketService.market.id },
      this.ajax.host,
    );

    const requestBody = {
      actions: this.dirtyItems.map((item) => ({
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        subscription_id: parseInt(item.subscription.id, 10),
        transaction_code: item.transactionCode,
        our_category: item.ourCategory,
        our_sub_category: item.ourSubCategory,
      })),
    };

    try {
      const mappingResponse = await this.ajax.request<object>(mappingUrl, {
        type: 'POST',
        data: JSON.stringify(requestBody),
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('transaction-mapping/item', mappingResponse);

      // TODO TOR-351: check what message we want to show
      this.toast.success(this.intl.t('general.toast.successfully_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    } finally {
      this.usageAnalytics.track('Transaction Code Mapping Saved', {
        eventTrigger: 'transaction-mapping',
      });
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dirtyItems() {
    return this.mappingItems.filter((item) => item.hasDirtyAttributes);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredMappingItems() {
    let filteredItems = this.mappingItems;

    if (this.isOnlyUnmappedShown) {
      filteredItems = this.mappingItems.filter((item) => item.isUnmapped);
    }

    Object.values(FILTER_IDS).forEach((filterId) => {
      filteredItems = this.filterItems(filteredItems, filterId);
    });

    return filteredItems;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterOptions() {
    return Object.values(FILTER_IDS).map((filterId) => {
      const uniqueValues = Array.from(
        new Set(this.mappingItems.map((item) => item[filterId])),
      );

      const options = uniqueValues.map((value) => ({
        id: filterId,
        label: value,
      }));

      return {
        groupName: this.intl.t(FILTER_TRANSLATION_KEYS[filterId]),
        unsafe_isCollapsed: true,
        options,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasUnsavedChanges() {
    return this.dirtyItems.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hierarchyCategories() {
    return [...(this.model.hierarchy.last?.value ?? [])];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFooterButtonsDisabled() {
    return !this.hasUnsavedChanges || this.saveTask.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappingItems() {
    return [...(this.model.mapping.last?.value ?? [])];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfItems() {
    return this.mappingItems.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  filterItems(items: TransactionMappingItem[], filterId: FilterId) {
    const filtersForId = this.filters
      .filter((filter) => filter.id === filterId)
      .map((filter) => filter.label);

    if (filtersForId.length) {
      return items.filter((item) => filtersForId.includes(item[filterId]));
    }

    return items;
  }

  @action discardChanges(): void {
    this.mappingItems.forEach((item) => item.rollbackAttributes());

    this.usageAnalytics.track('Transaction Code Mapping Cancelled', {
      eventTrigger: 'transaction-mapping',
    });
  }

  @action mapItems(
    items: TransactionMappingItem[],
    category: Category,
    subCategory: SubCategory,
  ): void {
    const numberOfItemsBeforeChange = this.filteredMappingItems.length;

    items.forEach((item) => {
      item.ourCategory = category;
      item.ourSubCategory = subCategory;
    });

    const numberOfItemsAfterChange = this.filteredMappingItems.length;

    if (numberOfItemsAfterChange < numberOfItemsBeforeChange) {
      this.toast.success(
        this.intl.t('general.mapping.mapped_success_rows_hidden'),
        {
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        },
      );
    }

    this.usageAnalytics.track('Transaction Code Mapping Changed', {
      eventTrigger: 'transaction-mapping',
      category,
      subCategory,
      numberOfItems: items.length,
    });
  }

  @action
  async onCancel(): Promise<void> {
    const isCancelConfirmed = await this.showDiscardChangesAlert();

    if (!isCancelConfirmed) {
      return;
    }

    this.discardChanges();
  }

  @action onRemoveFilterClicked(removedFilter: FilterOption): void {
    this.filters = this.filters.filter(
      (filter) =>
        !(
          filter.id === removedFilter.id && filter.label === removedFilter.label
        ),
    );
  }

  @action setFilters(filters: FilterOption[]): void {
    this.filters = filters;

    const filterTypes = Array.from(new Set(filters.map((filter) => filter.id)));
    this.usageAnalytics.track('Transaction Code Mapping Settings Filtered', {
      eventTrigger: 'transaction-filter',
      filterTypes,
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  showDiscardChangesAlert() {
    return this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('central_mapping.confirmation_modal.title'),
      text: this.intl.t('central_mapping.confirmation_modal.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'central_mapping.confirmation_modal.action.primary',
          ),
        },
      },
    });
  }

  @action toggleOnlyUnmapped(isEnabled: boolean): void {
    this.isOnlyUnmappedShown = isEnabled;

    this.usageAnalytics.track(
      'Transaction Code Mapping Settings Unmapped Only Changed',
      {
        eventTrigger: 'transaction-unmapped',
        isEnabled: this.isOnlyUnmappedShown,
      },
    );
  }
}
